import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Square, ArrowLeft, Activity, Zap, Timer as TimerIcon, ArrowRight, Info, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Nav from "@/components/Nav";

const DURATIONS = [
  { label: "2 min", sec: 120 },
  { label: "5 min", sec: 300 },
  { label: "10 min", sec: 600 },
];

const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function Player() {
  const { programId } = useParams();
  const nav = useNavigate();
  const [program, setProgram] = useState(null);
  const [phase, setPhase] = useState("setup"); // setup | running | done
  const [duration, setDuration] = useState(300);
  const [timeLeft, setTimeLeft] = useState(300);
  const [focus, setFocus] = useState(45);
  const [breath, setBreath] = useState("Breathe in");
  const [breathFill, setBreathFill] = useState(0);
  const [breathSecs, setBreathSecs] = useState(0);
  const [soundOn, setSoundOn] = useState(true);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const canvasRef = useRef(null);
  const focusRef = useRef(45);
  const samplesRef = useRef([]);
  const startRef = useRef(0);
  const rafRef = useRef(null);
  const lastSampleRef = useRef(0);
  const durationRef = useRef(300);
  const cycleRef = useRef(12); // breathing cycle length in seconds
  const audioCtxRef = useRef(null);
  const soundOnRef = useRef(true);
  const lastPhaseRef = useRef("");

  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);
  useEffect(() => () => { if (audioCtxRef.current) audioCtxRef.current.close(); }, []);

  const playBreathTone = useCallback((ph) => {
    const ctx = audioCtxRef.current;
    if (!ctx || !soundOnRef.current) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    const [f1, f2] = ph === "Breathe in" ? [220, 330] : [330, 220];
    osc.frequency.setValueAtTime(f1, now);
    osc.frequency.linearRampToValueAtTime(f2, now + 0.8);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.05, now + 0.1);
    gain.gain.linearRampToValueAtTime(0, now + 0.95);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 1);
  }, []);

  useEffect(() => {
    if (programId) api.get(`/programs/${programId}`).then((r) => {
      setProgram(r.data);
      cycleRef.current = /deep flow/i.test(r.data.name || "") ? 16 : 12;
    }).catch(() => {});
  }, [programId]);

  const draw = useCallback((ctx, w, h, elapsed) => {
    ctx.clearRect(0, 0, w, h);
    const f = focusRef.current;
    const cx = w / 2, cy = h / 2;

    // breathing scale — 5s inhale / 5s exhale (10s cycle)
    const cyc = (elapsed % cycleRef.current) / cycleRef.current;
    const breathScale = cyc < 0.5 ? Math.sin((cyc / 0.5) * (Math.PI / 2)) : Math.cos(((cyc - 0.5) / 0.5) * (Math.PI / 2));
    const baseR = Math.min(w, h) * 0.13;
    const r = baseR + breathScale * baseR * 0.9;

    // glow ring
    const grad = ctx.createRadialGradient(cx, cy, r * 0.2, cx, cy, r * 2.4);
    grad.addColorStop(0, `rgba(0,229,255,${0.12 + (f / 100) * 0.25})`);
    grad.addColorStop(1, "rgba(0,229,255,0)");
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.arc(cx, cy, r * 2.4, 0, Math.PI * 2); ctx.fill();

    ctx.strokeStyle = `rgba(0,229,255,${0.5 + (f / 100) * 0.5})`;
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();

    ctx.strokeStyle = "rgba(167,243,208,0.25)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(cx, cy, r + 14, 0, Math.PI * 2); ctx.stroke();

    // waveform — amplitude & frequency driven by focus
    const amp = (h * 0.16) * (0.3 + (f / 100) * 0.9);
    const freq = 0.012 + (f / 100) * 0.02;
    ctx.beginPath();
    for (let x = 0; x <= w; x += 4) {
      const env = Math.exp(-Math.pow((x - cx) / (w * 0.42), 2));
      const y = cy + Math.sin(x * freq + elapsed * 2.2) * amp * env
                   + Math.sin(x * freq * 2.3 + elapsed * 3.1) * amp * 0.35 * env;
      x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.strokeStyle = `rgba(0,229,255,${0.35 + (f / 100) * 0.45})`;
    ctx.lineWidth = 2;
    ctx.shadowColor = "rgba(0,229,255,0.6)";
    ctx.shadowBlur = 14;
    ctx.stroke();
    ctx.shadowBlur = 0;
  }, []);

  const finish = useCallback(async () => {
    cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) { audioCtxRef.current.close(); audioCtxRef.current = null; }
    const s = samplesRef.current;
    const avg = s.length ? s.reduce((a, b) => a + b, 0) / s.length : focusRef.current;
    const best = s.length ? Math.max(...s) : focusRef.current;
    const res = { avg: Math.round(avg), best: Math.round(best), duration: durationRef.current };
    setResult(res);
    setPhase("done");
    setSaving(true);
    try {
      await api.post("/sessions", {
        program_id: programId || null,
        program_name: program?.name || "Free session",
        duration_sec: durationRef.current,
        avg_focus: res.avg,
        best_focus: res.best,
      });
    } catch { toast.error("Could not save session"); }
    finally { setSaving(false); }
  }, [programId, program]);

  const start = () => {
    durationRef.current = duration;
    setTimeLeft(duration);
    focusRef.current = 45;
    samplesRef.current = [];
    startRef.current = performance.now();
    lastSampleRef.current = 0;
    lastPhaseRef.current = "";
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioCtxRef.current = new AC();
    }
    if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();
    setPhase("running");
  };

  // animation + simulated feedback loop
  useEffect(() => {
    if (phase !== "running") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      ctx.setTransform(window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const loop = (now) => {
      const elapsed = (now - startRef.current) / 1000;
      const cyc = (elapsed % cycleRef.current) / cycleRef.current;
      // exhale (second half) nudges focus up — rewards slow breathing
      const exhaleBoost = cyc >= 0.5 ? 8 : 0;
      const progressBias = clamp((elapsed / durationRef.current) * 32, 0, 32); // improve over session
      const noise = (Math.sin(elapsed * 1.7) + Math.sin(elapsed * 0.6)) * 4 + (Math.random() - 0.5) * 5;
      const target = clamp(46 + progressBias + exhaleBoost + noise, 5, 100);
      focusRef.current = lerp(focusRef.current, target, 0.035);

      const rect = canvas.getBoundingClientRect();
      draw(ctx, rect.width, rect.height, elapsed);

      if (elapsed - lastSampleRef.current >= 1) {
        lastSampleRef.current = elapsed;
        samplesRef.current.push(focusRef.current);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(rafRef.current); window.removeEventListener("resize", resize); };
  }, [phase, draw]);

  // ui tick — focus number + breath label + breathing pace ring
  useEffect(() => {
    if (phase !== "running") return;
    const id = setInterval(() => {
      setFocus(Math.round(focusRef.current));
      const cycle = cycleRef.current;
      const elapsed = (performance.now() - startRef.current) / 1000;
      const cyc = (elapsed % cycle) / cycle;
      let label, fill, remaining;
      if (cyc < 0.45) { label = "Breathe in"; fill = cyc / 0.45; remaining = (0.45 - cyc) * cycle; }
      else if (cyc < 0.55) { label = "Hold"; fill = 1; remaining = (0.55 - cyc) * cycle; }
      else { label = "Breathe out"; fill = 1 - (cyc - 0.55) / 0.45; remaining = (1 - cyc) * cycle; }
      setBreath(label);
      setBreathFill(fill);
      setBreathSecs(Math.max(1, Math.ceil(remaining)));
      if (label !== lastPhaseRef.current) {
        if (label === "Breathe in" || label === "Breathe out") playBreathTone(label);
        lastPhaseRef.current = label;
      }
    }, 120);
    return () => clearInterval(id);
  }, [phase]);

  // countdown
  useEffect(() => {
    if (phase !== "running") return;
    const id = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(id); finish(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [phase, finish]);

  return (
    <div className="min-h-screen bg-soma-dashbg">
      <Nav />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-32 pb-24">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-soma-muted hover:text-white transition-colors mb-10">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <AnimatePresence initial={false}>
          {phase === "setup" && (
            <motion.div key="setup" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="max-w-2xl">
              <span className="text-xs uppercase tracking-[0.35em] text-soma-primary">Focus trainer</span>
              <h1 className="font-display font-semibold text-3xl lg:text-4xl mt-4 mb-4">
                {program ? program.name : "Neurofeedback session"}
              </h1>
              <p className="text-soma-muted leading-relaxed mb-3">
                Follow the breathing ring and hold your attention on the pulse. Your focus signal rises as you settle into a slow, steady rhythm.
              </p>
              <div className="flex items-start gap-2 text-xs text-soma-muted/80 mb-10">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Simulated biofeedback demo — connect a supported EEG headband to train on your live brainwave signal.</span>
              </div>

              <label className="text-xs uppercase tracking-[0.2em] text-soma-muted">Session length</label>
              <div className="grid grid-cols-3 gap-3 mt-3 mb-10">
                {DURATIONS.map((d) => (
                  <button key={d.sec} onClick={() => setDuration(d.sec)} data-testid={`duration-${d.sec}`}
                    className={`py-5 border transition-colors duration-300 ${duration === d.sec ? "border-soma-primary bg-soma-primary/10 text-white" : "border-soma-border bg-soma-surface text-soma-muted hover:border-white/20"}`}>
                    <TimerIcon className="w-4 h-4 mx-auto mb-2" />
                    <span className="font-display font-semibold">{d.label}</span>
                  </button>
                ))}
              </div>

              <button onClick={start} data-testid="start-session"
                className="flex items-center gap-3 px-10 py-4 rounded-full bg-soma-primary text-soma-bg font-semibold hover:bg-soma-primaryHover transition-colors duration-300 cyan-glow">
                <Play className="w-4 h-4" fill="currentColor" /> Begin session
              </button>
            </motion.div>
          )}

          {phase === "running" && (
            <motion.div key="run" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2 text-soma-primary">
                  <Activity className="w-5 h-5" />
                  <span className="font-display font-semibold">{program ? program.name : "Focus session"}</span>
                </div>
                <div className="flex items-center gap-4">
                  <button onClick={() => setSoundOn((s) => !s)} data-testid="sound-toggle"
                    title="Toggle breathing sound" className="text-soma-muted hover:text-white transition-colors">
                    {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </button>
                  <div className="font-display font-semibold text-2xl tabular-nums" data-testid="session-timer">{fmt(timeLeft)}</div>
                </div>
              </div>

              <div className="relative border border-soma-border bg-soma-bg overflow-hidden" style={{ height: "min(52vh, 460px)" }}>
                <canvas ref={canvasRef} className="w-full h-full block" />
                <div className="absolute inset-x-0 top-6 flex flex-col items-center pointer-events-none" data-testid="breath-guide">
                  <div className="relative w-16 h-16">
                    <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                      <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
                      <circle cx="32" cy="32" r="28" fill="none" stroke="#00E5FF" strokeWidth="3" strokeLinecap="round"
                        strokeDasharray={2 * Math.PI * 28}
                        strokeDashoffset={(2 * Math.PI * 28) * (1 - breathFill)}
                        style={{ transition: "stroke-dashoffset 120ms linear", filter: "drop-shadow(0 0 6px rgba(0,229,255,0.6))" }} />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-soma-primary font-display font-semibold text-sm tabular-nums" data-testid="breath-count">{breathSecs}</span>
                    </div>
                  </div>
                  <motion.span key={breath} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    className="mt-3 font-serif-editorial italic text-lg text-white/90" data-testid="breath-label">{breath}</motion.span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-soma-border border border-soma-border border-t-0">
                <div className="bg-soma-dashbg p-6" data-testid="focus-score">
                  <div className="flex items-center gap-2 text-soma-muted text-xs uppercase tracking-[0.2em] mb-2"><Zap className="w-3.5 h-3.5" /> Focus</div>
                  <div className="font-display font-semibold text-4xl text-soma-primary tabular-nums">{focus}<span className="text-lg text-soma-muted">/100</span></div>
                </div>
                <div className="bg-soma-dashbg p-6 sm:col-span-2 flex flex-col justify-center">
                  <div className="h-2 bg-soma-border rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-soma-accent to-soma-primary transition-[width] duration-200" style={{ width: `${focus}%` }} />
                  </div>
                  <button onClick={finish} data-testid="end-session"
                    className="mt-5 self-start flex items-center gap-2 text-sm text-soma-muted hover:text-red-400 transition-colors">
                    <Square className="w-3.5 h-3.5" /> End session early
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {phase === "done" && result && (
            <motion.div key="done" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl" data-testid="session-summary">
              <div className="w-16 h-16 rounded-full bg-soma-primary/15 flex items-center justify-center mb-8 cyan-glow">
                <Activity className="w-8 h-8 text-soma-primary" strokeWidth={1.5} />
              </div>
              <span className="text-xs uppercase tracking-[0.35em] text-soma-primary">Session complete</span>
              <h1 className="font-display font-semibold text-3xl lg:text-4xl mt-4 mb-8">Nicely done.</h1>

              <div className="grid grid-cols-3 gap-px bg-soma-border border border-soma-border mb-10">
                <div className="bg-soma-surface p-6">
                  <div className="font-display font-semibold text-4xl text-white">{result.avg}</div>
                  <div className="text-xs text-soma-muted mt-1">Avg focus</div>
                </div>
                <div className="bg-soma-surface p-6">
                  <div className="font-display font-semibold text-4xl text-soma-primary">{result.best}</div>
                  <div className="text-xs text-soma-muted mt-1">Peak focus</div>
                </div>
                <div className="bg-soma-surface p-6">
                  <div className="font-display font-semibold text-4xl text-white">{Math.round(result.duration / 60)}m</div>
                  <div className="text-xs text-soma-muted mt-1">Duration</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/dashboard" data-testid="summary-dashboard"
                  className="flex items-center gap-2 px-8 py-4 rounded-full bg-soma-primary text-soma-bg font-semibold hover:bg-soma-primaryHover transition-colors">
                  View progress <ArrowRight className="w-4 h-4" />
                </Link>
                <button onClick={() => { setPhase("setup"); setFocus(45); }} data-testid="again"
                  className="px-8 py-4 rounded-full border border-white/15 hover:border-soma-primary hover:text-soma-primary transition-colors">
                  Train again
                </button>
              </div>
              {saving && <p className="text-xs text-soma-muted mt-4">Saving your session…</p>}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
